import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, LineChart, BarChart2, BarChart3, Newspaper, TrendingUp, TrendingDown, RefreshCw } from "lucide-react";
import axios from "axios";
import "./SecondWireframeSection.scss";
import DashboardStyleTicker from "./DashboardStyleTicker";

const defaultTrendingCards = [
  {
    name: "COALINDIA.NS",
    price: "₹412.75",
    change: "+0.9% today",
    isPositive: true,
    stats: [
      { label: "P/E Ratio", value: "14.40" },
      { label: "Volume", value: "25115469" },
    ]
  },
];

const SecondWireframeSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [trendingCards, setTrendingCards] = useState(defaultTrendingCards);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const handleNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % trendingCards.length);
  }, [trendingCards.length]);

  const handlePrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + trendingCards.length) % trendingCards.length);
  }, [trendingCards.length]);

  useEffect(() => {
    const interval = setInterval(handleNext, 4000);
    return () => clearInterval(interval);
  }, [handleNext]);

  // Fetch stock data from stock_data.json
  useEffect(() => {
    const fetchStockData = async () => {
      try {
        const baseUrl = process.env.REACT_APP_COMPANY_DOC;
        if (!baseUrl) {
          console.error("Missing REACT_APP_COMPANY_DOC environment variable");
          setIsLoading(false);
          return;
        }

        const url = `${baseUrl}/website_v2/stock_data.json`;
        console.log("[trending] fetching from", url);
        
        const response = await axios.get(url);
        const stockDataArray = response?.data?.data || [];

        // Transform API data to card format
        const cards = stockDataArray.slice(0, 5).map((stock: any) => {
          const ltp = stock.ltp || 0;
          const prevClose = stock.prev_close || ltp;
          const dayChangePercent = prevClose !== 0 ? ((ltp - prevClose) / prevClose) * 100 : 0;
          const isPositive = ltp >= prevClose;
          
          console.log(`[Stock: ${stock.ticker}] LTP: ${ltp}, PrevClose: ${prevClose}, ChangePercent: ${dayChangePercent}`);
          
          return {
            name: stock.ticker,
            price: `₹${stock.ltp?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 'N/A'}`,
            change: `${isPositive ? '+' : ''}${dayChangePercent.toFixed(2)}% today`,
            isPositive: isPositive,
            stats: [
              { label: "P/E Ratio", value: stock.pe_ratio?.toFixed(2) || "N/A" },
              { label: "Volume", value: stock.volume?.toLocaleString('en-IN') || "N/A" },
              // { label: "Dividend Yield", value: `${(stock.dividend_yield || 0).toFixed(2)}%` },
            ]
          };
        });

        // Only replace the fallback cards when the fetch actually returned data,
        // otherwise keep the defaults so the section never renders empty.
        if (cards.length > 0) {
          setTrendingCards(cards);
        }
        setIsLoading(false);
      } catch (error) {
        console.error("[trending] fetch failed", error);
        setIsLoading(false);
      }
    };

    fetchStockData();
  }, []);

  // Guard against an empty list: `% 0` yields NaN and would push `undefined`
  // cards, crashing the render at `card.isPositive`. Capping the count at the
  // list length also avoids duplicate React keys when there are fewer than 3.
  const visibleCards = [];
  if (trendingCards.length > 0) {
    const count = Math.min(3, trendingCards.length);
    for (let i = 0; i < count; i++) {
      visibleCards.push(trendingCards[(activeIndex + i) % trendingCards.length]);
    }
  }

  if (isLoading) {
    return (
      <section className="second-wireframe-section">
        <DashboardStyleTicker />
        <div className="wireframe-content">
          <div className="left-market-panel">
            <h2>What&apos;s Trending?</h2>
            <p>Loading market signals...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="second-wireframe-section">
      <DashboardStyleTicker />
      <div className="wireframe-content">
        <div className="left-market-panel">
          {/* <span className="live-badge">
            <BarChart2 size={14} className="badge-icon" />
            LIVE MARKET
          </span> */}
          <h2>What&apos;s Trending?</h2>
          <p>Live market signals updated in real-time</p>

          <div className="stock-cards-row">
            {visibleCards.map((card) => (
              <div key={card.name} className={`stock-card ${card.isPositive ? "positive-card" : "negative-card"}`}>
                <div className="card-header">
                  <h4>{card.name}</h4>
                  {card.isPositive ? (
                    <TrendingUp size={16} color="#10b981" />
                  ) : (
                    <TrendingDown size={16} color="#ef4444" />
                  )}
                </div>
                <h3>{card.price}</h3>
                <p className={`card-change ${card.isPositive ? "text-green" : "text-red"}`}>
                  {card.change}
                </p>
                <hr className="card-divider" />
                <div className="card-stats">
                  {card.stats.map((stat, sIdx) => (
                    <div key={sIdx} className="stat">
                      <span className="stat-label">{stat.label}</span>
                      <strong className="stat-value">{stat.value}</strong>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="carousel-controls">
            <button className="nav-btn" aria-label="Previous" onClick={handlePrev}>
              <ChevronLeft size={16} />
            </button>
            <div className="dots">
              {trendingCards.map((_, dotIdx) => (
                <span 
                  key={dotIdx} 
                  className={dotIdx === activeIndex ? "active" : ""}
                  onClick={() => setActiveIndex(dotIdx)}
                  style={{ cursor: "pointer" }}
                ></span>
              ))}
            </div>
            <button className="nav-btn" aria-label="Next" onClick={handleNext}>
              <ChevronRight size={16} />
            </button>
            {/* <button className="live-pill-btn">
              <RefreshCw size={14} /> Live
            </button> */}
          </div>
        </div>

        <div className="right-intelligence-panel">
          <h2>Powered by Intelligence</h2>
          <p>Everything you need to make informed decisions</p>

          <div className="insight-grid">
            <article className="insight-card primary">
              <div className="icon-wrap">
                <TrendingUp size={18} />
              </div>
              <div>
                <h3>Real-time Market Insights</h3>
                <p>
                  Track stock prices, live changes, and volumes instantly
                </p>
              </div>
            </article>

            <article className="insight-card">
              <div className="icon-wrap">
                <LineChart size={18} />
              </div>
              <div>
                <h3>Technical Alerts</h3>
                <p>From Price Changes to Open Prices to Volumes, to Momentum — get all at one place.</p>
              </div>
            </article>

            <article className="insight-card">
              <div className="icon-wrap">
                <BarChart3 size={18} />
              </div>
              <div>
                <h3>Market Intelligence</h3>
                <p>Top stories and curated news, always relevant.</p>
              </div>
            </article>
          </div>

          <button className="explore-btn" onClick={() => navigate("/trending")}>
            <Newspaper size={16} /> Explore All Trends
          </button>
        </div>
      </div>
    </section>
  );
};

export default SecondWireframeSection;
